import { type FC, useMemo } from "react";
import { useMembersByIds, type PlainOrganizationMember } from "@/queries/organization-queries";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { Text } from "@/components/podkit/typography/Text";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials, type AvatarSize } from "@/components/Avatar";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { Tooltip } from "@/components/Tooltip";
import { ErrorMessage } from "@/components/ErrorMessage";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { CronSelector } from "@/components/flexkit/CronSelector";
import IconTriangleExclamation from "central-icons/IconTriangleExclamation";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { getUserId } from "@/utils/subjects";
import type { Workflow, WorkflowTrigger, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import { pluralize } from "@/utils/strings";
import { Button } from "@/components/flexkit/Button";
import IconPencil from "central-icons/IconPencil";
import { withGitpodSize } from "@/assets/icons/utils";
import { isWorkflowDeleting } from "@/utils/automations";

const IconPencilWithSize = withGitpodSize(IconPencil);

type ExecutorInfo = {
    name: string;
    avatar: React.ReactNode;
};

const UserAvatar: FC<{ member: PlainOrganizationMember; size?: AvatarSize }> = ({ member, size = 16 }) => (
    <Avatar size={size} className="border border-border-base">
        {member.avatarUrl && <AvatarImage src={member.avatarUrl} alt={`${member.fullName}'s avatar`} />}
        <AvatarFallback>
            <AvatarInitials name={member.fullName} size={size} />
        </AvatarFallback>
    </Avatar>
);

type AutomationPropertiesHeaderProps = {
    workflow: PlainMessage<Workflow>;
    editable: boolean;
    onEdit?: () => void;
};

export const AutomationPropertiesHeader: FC<AutomationPropertiesHeaderProps> = ({ workflow, editable, onEdit }) => {
    const firstTrigger = workflow.spec?.triggers?.[0];
    const isDeleting = isWorkflowDeleting(workflow);

    return (
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <CreatorAndExecutorLabel workflow={workflow} />
            <TriggerTypeLabel trigger={firstTrigger} />
            <TriggerScopeLabel trigger={firstTrigger} />
            <TriggerLimitsLabel limits={workflow.spec?.action?.limits} />
            {editable ? (
                <Button
                    size="xs"
                    variant="ghost"
                    // optical alignment with the text
                    className="-translate-y-[1.5px]"
                    LeadingIcon={IconPencilWithSize}
                    onClick={onEdit}
                    disabled={isDeleting}
                    data-tracking-id="edit"
                >
                    <span className="sr-only">Edit</span>
                </Button>
            ) : null}
        </div>
    );
};

const Separator: FC = () => {
    return <span className="text-content-inactive">•</span>;
};

const TriggerTypeLabel: FC<{ trigger?: PlainMessage<WorkflowTrigger> }> = ({ trigger }) => {
    if (!trigger?.trigger) {
        return <Text>No configured trigger.</Text>;
    }

    switch (trigger.trigger.case) {
        case "manual":
            return <Text className="text-sm font-medium">Manual</Text>;
        case "time":
            return (
                <div className="flex items-center gap-1.5">
                    <Text className="text-sm text-content-muted">Scheduled:</Text>
                    <CronSelector value={trigger.trigger.value.cronExpression} readOnly={true} showTimezone={true} />
                </div>
            );
        case "pullRequest": {
            const eventCount = Number(trigger.trigger.value.events.length);
            return (
                <Text className="text-content-muted">
                    Pull Request:{" "}
                    <span className="font-medium text-content-primary">
                        {eventCount} {pluralize(eventCount, "event")}
                    </span>
                </Text>
            );
        }
        default:
            return <>Unknown</>;
    }
};

const TriggerScopeLabel: FC<{ trigger?: PlainMessage<WorkflowTrigger> }> = ({ trigger }) => {
    if (!trigger?.context?.context) {
        return null;
    }

    switch (trigger.context.context.case) {
        case "repositories": {
            if (trigger.context.context.value.repositorySelector.case !== "repositoryUrls") {
                return null;
            }
            const repoCount = Number(trigger.context.context.value.repositorySelector.value.repoUrls.length);
            return (
                <>
                    <Separator />
                    <Text className="text-content-muted">
                        {repoCount} {pluralize(repoCount, "repository", "repositories")}
                    </Text>
                </>
            );
        }
        case "projects": {
            const projectCount = Number(trigger.context.context.value.projectIds.length);
            return (
                <>
                    <Separator />
                    <Text className="text-content-muted">
                        {projectCount} {pluralize(projectCount, "project")}
                    </Text>
                </>
            );
        }
        default:
            return null;
    }
};

const getCreatorMember = (
    workflow: PlainMessage<Workflow>,
    members?: PlainOrganizationMember[],
): PlainOrganizationMember | undefined => {
    const creator = workflow.metadata?.creator;
    if (!creator?.id || !members) return undefined;
    return members.find((m) => m.userId === creator.id);
};

const getExecutorInfo = (
    workflow: PlainMessage<Workflow>,
    members?: PlainOrganizationMember[],
    serviceAccountsData?: { serviceAccounts: { id: string; name: string }[] },
): ExecutorInfo | null => {
    const executor = workflow.metadata?.executor;
    if (!executor) return null;

    if (executor.principal === Principal.USER && members) {
        const executorMember = members.find((m) => m.userId === executor.id);
        if (executorMember) {
            return {
                name: executorMember.fullName,
                avatar: <UserAvatar member={executorMember} />,
            };
        }
    } else if (executor.principal === Principal.SERVICE_ACCOUNT && serviceAccountsData) {
        const serviceAccount = serviceAccountsData.serviceAccounts.find((sa) => sa.id === executor.id);
        if (serviceAccount) {
            return {
                name: serviceAccount.name,
                avatar: <ServiceAccountAvatar className="p-0.5" id={serviceAccount.id} size={16} />,
            };
        }
    }

    return null;
};

const CreatorAndExecutorLabel: FC<{ workflow: PlainMessage<Workflow> }> = ({ workflow }) => {
    const {
        data: serviceAccountsData,
        isLoading: isLoadingServiceAccounts,
        error: listServiceAccountsError,
    } = useListServiceAccounts({
        enabled: workflow.metadata?.executor?.principal === Principal.SERVICE_ACCOUNT,
    });

    const userIds = useMemo(() => {
        const ids: string[] = [];
        const creatorId = workflow.metadata?.creator?.id;
        if (creatorId) ids.push(creatorId);
        const executorUserId = getUserId(workflow.metadata?.executor);
        if (executorUserId) ids.push(executorUserId);
        return ids;
    }, [workflow.metadata?.creator, workflow.metadata?.executor]);

    const { members: membersMap, isLoading: isLoadingMembers, isError: isMembersError } = useMembersByIds(userIds);

    const isLoading = isLoadingMembers || isLoadingServiceAccounts;
    const error = listServiceAccountsError || (isMembersError ? new Error("Failed to fetch members") : null);

    if (error) {
        return (
            <>
                <SkeletonText className="w-48" size="sm" ready={!isLoading}>
                    <ExecutorErrorTooltip error={error} />
                </SkeletonText>
                <Separator />
            </>
        );
    }

    if (isLoadingMembers) {
        return (
            <>
                <SkeletonText className="w-48" size="sm" ready={false}>
                    {null}
                </SkeletonText>
                <Separator />
            </>
        );
    }

    const membersArray = Array.from(membersMap.values());
    const creatorMember = getCreatorMember(workflow, membersArray);
    const executorInfo = getExecutorInfo(workflow, membersArray, serviceAccountsData);

    const creator = workflow.metadata?.creator;
    const executor = workflow.metadata?.executor;
    const isSameUser = creator?.id && executor?.id && creator.id === executor.id;

    let content: React.ReactNode = null;

    if (isSameUser && creatorMember) {
        content = (
            <Text className="flex items-center gap-1 text-content-muted">
                Author / Runs as:
                <span>
                    <UserAvatar member={creatorMember} />
                </span>
                <span className="font-medium text-content-primary">{creatorMember.fullName}</span>
            </Text>
        );
    } else if (creatorMember || executorInfo) {
        content = (
            <Text className="flex items-center gap-1 text-content-muted">
                {creatorMember && (
                    <>
                        Author:
                        <span>
                            <UserAvatar member={creatorMember} />
                        </span>
                        <span className="font-medium text-content-primary">{creatorMember.fullName}</span>
                    </>
                )}
                {creatorMember && executorInfo && <Separator />}
                {executorInfo && (
                    <>
                        Runs as:
                        <span>{executorInfo.avatar}</span>
                        <span className="font-medium text-content-primary">{executorInfo.name}</span>
                    </>
                )}
            </Text>
        );
    }

    if (!content) {
        return null;
    }

    return (
        <>
            <SkeletonText className="w-48" size="sm" ready={!isLoading}>
                {content}
            </SkeletonText>
            <Separator />
        </>
    );
};

const ExecutorErrorTooltip: FC<{ error: Error }> = ({ error }) => {
    return (
        <Tooltip side="bottom" content={<ErrorMessage className="text-content-destructive-subtle" error={error} />}>
            <Text className="flex select-none items-center text-content-destructive">
                Executor info unavailable
                <IconTriangleExclamation className="ml-1" size={16} />
            </Text>
        </Tooltip>
    );
};

const TriggerLimitsLabel: FC<{ limits?: PlainMessage<WorkflowAction_Limits> }> = ({ limits }) => {
    if (!limits) {
        return null;
    }

    const maxParallel = Number(limits.maxParallel);
    const maxTotal = Number(limits.maxTotal);

    if (maxParallel === 0 && maxTotal === 0) {
        return (
            <>
                <Separator />
                <Text className="text-content-muted">No concurrent or max action limits</Text>
            </>
        );
    }

    const concurrentLimitLabel = maxParallel === 0 ? "No concurrent" : `${maxParallel} concurrent`;
    const actionLimitLabel = maxTotal === 0 ? "no action limit" : `max ${maxTotal} ${pluralize(maxTotal, "action")}`;

    return (
        <>
            <Separator />
            <Text className="text-content-muted">
                <span className="font-medium text-content-primary">{concurrentLimitLabel} </span>and
                <span className="font-medium text-content-primary"> {actionLimitLabel}</span>
            </Text>
        </>
    );
};
